import { generatePath } from 'react-router-dom';
import { arrayUtils, textUtils } from '@sato/components';

import { OwnApartment } from '@models/apartment.types';
import { LanguageKey } from '@models/Language';
import { RealEstateDocument } from '@models/realEstate.types';
import {
  SATO_FI_BASE_URL,
  SATO_FI_ROUTES,
  SatoFIRoute,
} from '@routes/routes.constants';
import { RouteParams, SatoFiRouteParams } from '@routes/routes.types';

import { isLocalEnv } from './featureFlags';

const formatSatoFiRouteParam = <T extends string | number>(param: T) =>
  textUtils.toLowerCase(
    encodeURIComponent(textUtils.toLowerCase(param.toString()))
  ) as Lowercase<`${T}`>;

export const getRouteParamsForApartmentPage = (props: {
  municipality: string;
  district: string;
  streetAddress: string;
  realEstateId: string;
  productId: string;
  language: LanguageKey;
}) =>
  ({
    municipality: formatSatoFiRouteParam(props.municipality),
    district: formatSatoFiRouteParam(props.district),
    realEstateStreetAddress: formatSatoFiRouteParam(props.streetAddress),
    realEstateId: formatSatoFiRouteParam(props.realEstateId),
    id: formatSatoFiRouteParam(props.productId),
    language: props.language,
  }) satisfies SatoFiRouteParams<SatoFIRoute.URL_APARTMENT_PAGE>;

const getRouteParamsForRealEstatePage = (
  realEstate: RealEstateDocument,
  language: LanguageKey
) =>
  ({
    municipality: realEstate.areas.municipality?.name ?? 'municipality',
    district: realEstate.areas.area?.name ?? 'district',
    streetAddress: realEstate.address.streetAddress ?? 'address',
    id: realEstate.id,
    language,
  }) satisfies SatoFiRouteParams<SatoFIRoute.URL_REAL_ESTATE_PAGE>;

const getApartmentPageUrl = (
  { apartment, realestate }: OwnApartment,
  language: LanguageKey
) => {
  const routeParams = apartment.productId
    ? getRouteParamsForApartmentPage({
        district: realestate.areas.area.name,
        municipality: realestate.areas.municipality.name,
        language,
        productId: apartment.productId,
        realEstateId: realestate.id,
        streetAddress:
          apartment.streetAddress ?? realestate.address.streetAddress,
      })
    : undefined;

  // early-exit if valid route params could not be structured
  if (!routeParams) return undefined;

  return getSatoFIUrl(SatoFIRoute.URL_APARTMENT_PAGE, routeParams);
};

const getRealEstatePageUrl = (
  { realestate }: OwnApartment,
  language: LanguageKey
) =>
  getSatoFIUrl(
    SatoFIRoute.URL_REAL_ESTATE_PAGE,
    getRouteParamsForRealEstatePage(realestate, language)
  );

const getApartmentUrls = (
  apartment: OwnApartment | undefined,
  language: LanguageKey
) => {
  if (!apartment) return {};

  return {
    apartmentUrl: getApartmentPageUrl(apartment, language),
    realEstateUrl: getRealEstatePageUrl(apartment, language),
  };
};

const getSatoFIUrl = <T extends SatoFIRoute, LanguageT extends LanguageKey>(
  routeName: T,
  routeParams: RouteParams<(typeof SATO_FI_ROUTES)[T][LanguageT]> & {
    language: LanguageT;
    hash?: string;
    search?: string;
  }
): string => {
  try {
    const routePath = SATO_FI_ROUTES[routeName];

    if (!routePath) return '#';

    const parameterizedRoutePath = generatePath(
      `/:language/${routePath[routeParams.language]}`,
      routeParams as any
    );

    return arrayUtils.joinArrayString(
      [
        SATO_FI_BASE_URL,
        parameterizedRoutePath,
        routeParams?.search,
        routeParams?.hash,
      ],
      ''
    );
  } catch (ex) {
    if (isLocalEnv) console.error('[getSatoFIUrl] error in parsing link', ex);

    return '#';
  }
};

export default {
  getApartmentUrls,
  getApartmentPageUrl,
  getRealEstatePageUrl,
  getSatoFIUrl,
  getRouteParamsForApartmentPage,
};
